import { useEffect, useMemo, useRef } from 'react';
import { useSupportAttachmentUrls } from '@/hooks/useSupportAttachmentUrls';
import { INLINE_ATTACHMENT_ATTRIBUTE, getInlineAttachmentIds, sanitizeSupportHtml } from '@/lib/supportHtml';
import { cn } from '@/lib/utils';

interface SupportHtmlContentProps {
  orgId: string;
  ticketId: string;
  html: string;
  /** IDs of inline images that have passed the malware scan and can be loaded. */
  cleanAttachmentIds: Set<string>;
  className?: string;
}

/**
 * Renders an HTML comment from support staff or the AI assistant: sanitized, with links opening in a
 * new tab, tables drawn with cell borders, and inline images loaded through the authenticated
 * attachment endpoint at the size they were given in Multiservice.
 */
export function SupportHtmlContent({ orgId, ticketId, html, cleanAttachmentIds, className }: SupportHtmlContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sanitized = useMemo(() => sanitizeSupportHtml(html), [html]);
  const inlineIds = useMemo(
    () => getInlineAttachmentIds(sanitized).filter((id) => cleanAttachmentIds.has(id)),
    [sanitized, cleanAttachmentIds]
  );
  const urls = useSupportAttachmentUrls(orgId, ticketId, inlineIds);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    container.querySelectorAll<HTMLImageElement>(`img[${INLINE_ATTACHMENT_ATTRIBUTE}]`).forEach((image) => {
      const id = image.getAttribute(INLINE_ATTACHMENT_ATTRIBUTE) ?? '';
      if (urls[id]) {
        image.src = urls[id];
      }
      // A height on its own would be overridden by the base image styles, so apply it directly.
      const height = image.getAttribute('height');
      if (height && !image.getAttribute('width') && !image.style.width && !image.style.height) {
        image.style.height = /^\d+(?:\.\d+)?$/.test(height) ? `${height}px` : height;
      }
    });
  }, [sanitized, urls]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'overflow-x-auto break-words text-sm leading-relaxed',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_img]:inline-block [&_img]:max-h-80 [&_img]:max-w-full [&_img]:object-contain',
        '[&_li]:ml-5 [&_ol]:list-decimal [&_ul]:list-disc [&_p]:mb-2 [&_p:last-child]:mb-0',
        '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
        '[&_td]:border [&_td]:p-2 [&_td]:align-middle [&_th]:border [&_th]:bg-muted [&_th]:p-2 [&_th]:text-left [&_th]:font-semibold',
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
